import { useEffect } from 'react';
import { safeJsonLd } from '../utils/sanitize';

/**
 * SEO Component for dynamic meta tags
 * Usage: <SEOHead title="Page Title" description="..." keywords="..." />
 */
export const SEOHead = ({ 
  title, 
  description, 
  keywords,
  canonicalPath,
  type = 'website',
  image,
  noindex = false
}) => {
  useEffect(() => {
    // Update document title
    if (title === null) {
      document.title = 'The Coppini Academy of Fine Art | Art Classes San Antonio TX';
    } else if (title) {
      const suffix = ' | The Coppini Academy of Fine Art';
      document.title = title.includes('Coppini') ? title : `${title}${suffix}`;
    } else {
      document.title = 'The Coppini Academy of Fine Art | Art Classes San Antonio TX';
    }
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && description) {
      metaDescription.setAttribute('content', description);
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords && keywords) {
      metaKeywords.setAttribute('content', keywords);
    }
    
    // Update canonical URL — always set to self-referencing canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalPath) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', `https://thecoppini.org${canonicalPath}`);
    } else if (canonicalLink) {
      // Remove stale canonical from a previous page if no canonicalPath provided
      canonicalLink.setAttribute('href', `https://thecoppini.org${window.location.pathname}`);
    }

    // Handle noindex for error pages
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else if (robotsMeta) {
      robotsMeta.remove();
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', document.title);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription && description) {
      ogDescription.setAttribute('content', description);
    }
    
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', type);
    }
    
  }, [title, description, keywords, canonicalPath, type, image, noindex]);
  
  return null;
};

/**
 * Schema.org Course structured data for class pages. `price`/`startDate` are
 * optional — omit rather than invent a value the page doesn't already show.
 */
export const CourseSchema = ({ name, description, provider = "The Coppini Academy of Fine Art", price, priceCurrency = "USD", startDate, endDate, instructor }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": name,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": provider,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "115 Melrose Place",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "postalCode": "78212",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio"
    }
  };

  const courseInstance = {};
  if (startDate) courseInstance.startDate = startDate;
  if (endDate) courseInstance.endDate = endDate;
  if (instructor) courseInstance.instructor = { "@type": "Person", "name": instructor };
  if (Object.keys(courseInstance).length > 0) {
    schema.hasCourseInstance = { "@type": "CourseInstance", "courseMode": "Onsite", ...courseInstance };
  }
  if (price !== undefined && price !== null && price !== '') {
    schema.offers = { "@type": "Offer", "price": String(price), "priceCurrency": priceCurrency };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
};

/**
 * Schema.org Event structured data for individual workshops. Requires a real
 * startDate from the page's own data — renders nothing rather than invent one.
 */
export const EventSchema = ({ name, description, startDate, endDate, price, priceCurrency = "USD", performer }) => {
  if (!startDate) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": name,
    "description": description,
    "startDate": startDate,
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "eventStatus": "https://schema.org/EventScheduled",
    "location": {
      "@type": "Place",
      "name": "The Coppini Academy of Fine Art",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "115 Melrose Place",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "postalCode": "78212",
        "addressCountry": "US"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": "The Coppini Academy of Fine Art",
      "url": "https://thecoppini.org"
    }
  };
  if (endDate) schema.endDate = endDate;
  if (performer) schema.performer = { "@type": "Person", "name": performer };
  if (price !== undefined && price !== null && price !== '') {
    schema.offers = {
      "@type": "Offer",
      "price": String(price),
      "priceCurrency": priceCurrency,
      "availability": "https://schema.org/InStock",
      "url": "https://thecoppini.org/workshops"
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
};

/**
 * FAQ Schema for pages with FAQ content
 */
export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
};

export default SEOHead;
